import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { dmClient } from '../../services/dmClient';
import usePageTitle from '../../hooks/usePageTitle';
import BundleForm from './BundleForm';
import { TABS, syncIds, validateAll } from './caseBuilderModel';
import './caseBuilder.css';

export default function CaseBuilderEditorPage() {
  usePageTitle('Edit case');
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [tabId, setTabId] = useState('case');
  const [sectionId, setSectionId] = useState(TABS[0].sections[0].id);
  const [record, setRecord] = useState(null);
  const [bundles, setBundles] = useState(null);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [coworkers, setCoworkers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const tab = TABS.find((item) => item.id === tabId) || TABS[0];
  const section = tab.sections.find((item) => item.id === sectionId) || tab.sections[0];

  useEffect(() => {
    let cancelled = false;
    dmClient.get(`/case-builder/cases/${caseId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data.data;
        setRecord(data);
        setBundles({
          case_bundle: data.case_bundle,
          claim_reference_bundle: data.claim_reference_bundle,
          case_resolution_bundle: data.case_resolution_bundle,
        });
        setSelected(data.share_user_ids || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Could not open this case.');
      });
    return () => { cancelled = true; };
  }, [caseId]);

  useEffect(() => {
    if (!showJson || !bundles) return;
    setJsonText(JSON.stringify(bundles[tab.key], null, 2));
    setJsonError('');
  }, [showJson, tab.key, bundles]);

  const payer = bundles?.case_bundle?.insurance?.payer_name;
  const claim = bundles?.case_bundle?.claim?.claim_id;
  const title = useMemo(() => payer || claim || 'Case', [payer, claim]);

  function updateDocument(nextDoc) {
    setBundles((current) => ({ ...current, [tab.key]: nextDoc }));
    setMessage('');
  }

  async function save() {
    const synced = syncIds(bundles);
    const result = validateAll(synced);
    if (result.count) {
      const hit = TABS.map((item) => ({ item, errors: result.byTab[item.id] || {} }))
        .find((entry) => Object.keys(entry.errors).length);
      if (hit) {
        const sectionHit = hit.item.sections.find((sec) => (
          Object.keys(hit.errors).some((key) => (sec.arrayPath ? key.startsWith(sec.arrayPath) : sec.fields.some((f) => key === f.path)))
        ));
        setTabId(hit.item.id);
        setSectionId((sectionHit || hit.item.sections[0]).id);
        setErrors(hit.errors);
      }
      setError('Fill the required fields before saving.');
      setBundles(synced);
      return false;
    }
    setErrors({});
    setBusy(true);
    setError('');
    try {
      const res = await dmClient.put(`/case-builder/cases/${caseId}`, synced);
      const data = res.data.data;
      setRecord(data);
      setBundles({
        case_bundle: data.case_bundle,
        claim_reference_bundle: data.claim_reference_bundle,
        case_resolution_bundle: data.case_resolution_bundle,
      });
      setMessage('Saved.');
      return true;
    } catch (err) {
      const details = err.response?.data?.details;
      setError(details ? details.slice(0, 4).join(' ') : (err.response?.data?.error || 'Save failed.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function duplicate() {
    setBusy(true);
    try {
      const res = await dmClient.post(`/case-builder/cases/${caseId}/duplicate`);
      navigate(`/case-builder/${res.data.data.canonical_case_id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not duplicate.');
    } finally {
      setBusy(false);
    }
  }

  async function processCase() {
    const saved = await save();
    if (!saved) return;
    setBusy(true);
    setError('');
    try {
      const res = await dmClient.post(`/case-builder/cases/${caseId}/executions`);
      navigate(`/case-builder/${caseId}/runs/${res.data.data.claim_resolution_execution_id}`, { state: { watch: true } });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start this run.');
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await dmClient.delete(`/case-builder/cases/${caseId}`);
      navigate('/case-builder');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete.');
      setBusy(false);
    }
  }

  async function openShare() {
    setShareOpen(true);
    try {
      const res = await dmClient.get('/case-builder/org-users');
      setCoworkers(res.data?.data?.items || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load coworkers.');
    }
  }

  async function saveShare() {
    setBusy(true);
    try {
      const res = await dmClient.put(`/case-builder/cases/${caseId}/shares`, { user_ids: selected });
      setSelected(res.data?.data?.user_ids || []);
      setShareOpen(false);
      setMessage('Sharing updated.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update sharing.');
    } finally {
      setBusy(false);
    }
  }

  function applyJson() {
    try {
      const parsed = JSON.parse(jsonText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setJsonError('JSON must be an object.');
        return;
      }
      updateDocument(parsed);
      setJsonError('');
      setShowJson(false);
    } catch (err) {
      setJsonError('That JSON could not be read. The form was not changed.');
    }
  }

  if (!bundles) {
    return <p className={error ? 'cb-error' : 'cb-note'}>{error || 'Loading case…'}</p>;
  }

  return (
    <div className="cb-editor">
      <header className="cb-head">
        <div>
          <h1>{title}</h1>
          <p>
            <Link to="/case-builder">Case Builder</Link>
            {' · '}{record?.case_key}
            {' · '}
            <span className={`cb-badge${record?.status === 'queued' ? ' is-queued' : ''}`}>
              {record?.status === 'queued' ? 'Queued' : 'Draft'}
            </span>
          </p>
        </div>
      </header>

      <div className="cb-tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`cb-tab${tabId === item.id ? ' is-on' : ''}`}
            onClick={() => { setTabId(item.id); setSectionId(item.sections[0].id); }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <p className="cb-blurb">{tab.blurb}</p>
      {error ? <p className="cb-error">{error}</p> : null}

      <div className="cb-layout">
        <nav className="cb-sidenav" aria-label="Sections">
          {tab.sections.map((item) => (
            <button
              key={item.id}
              type="button"
              className={section.id === item.id ? 'is-on' : ''}
              onClick={() => setSectionId(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="cb-main">
          <BundleForm
            section={section}
            document={bundles[tab.key]}
            errors={errors}
            onChange={updateDocument}
          />
          {showJson ? (
            <div className="cb-json">
              <textarea value={jsonText} onChange={(e) => setJsonText(e.target.value)} spellCheck={false} />
              {jsonError ? <p className="cb-error">{jsonError}</p> : null}
              <button type="button" className="dm-btn dm-btn--secondary" onClick={applyJson}>Apply JSON</button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="cb-sticky">
        <button type="button" className="dm-btn dm-btn--primary" onClick={save} disabled={busy}>Save</button>
        <button type="button" className="dm-btn dm-btn--secondary" onClick={duplicate} disabled={busy}>Duplicate</button>
        <button type="button" className="dm-btn dm-btn--secondary" onClick={processCase}>Process</button>
        {record?.is_owner ? (
          <button type="button" className="dm-btn dm-btn--secondary" onClick={openShare}>Share</button>
        ) : null}
        {record?.is_owner ? (
          <button type="button" className="dm-btn dm-btn--ghost" onClick={() => setConfirmDelete(true)}>Delete</button>
        ) : null}
        <button type="button" className="dm-btn dm-btn--ghost" onClick={() => setShowJson((v) => !v)}>JSON</button>
        <span className="cb-spacer">{message}</span>
      </div>

      {shareOpen ? (
        <div className="cb-modal-back">
          <div className="cb-modal" role="dialog" aria-modal="true">
            <h2>Share inside your company</h2>
            <p>Only people in your organization can open this case. Each person still keeps their own copy when they duplicate it.</p>
            <div className="cb-share-list">
              {coworkers.length === 0 ? <p>No other active users in this company.</p> : coworkers.map((user) => (
                <label className="cb-check" key={user.user_id}>
                  <input
                    type="checkbox"
                    checked={selected.includes(user.user_id)}
                    onChange={(e) => {
                      setSelected((current) => (
                        e.target.checked
                          ? current.concat(user.user_id)
                          : current.filter((id) => id !== user.user_id)
                      ));
                    }}
                  />
                  <span>{user.display_name || user.email}</span>
                </label>
              ))}
            </div>
            <div className="cb-modal__actions">
              <button type="button" className="dm-btn dm-btn--secondary" onClick={() => setShareOpen(false)}>Cancel</button>
              <button type="button" className="dm-btn dm-btn--primary" onClick={saveShare} disabled={busy}>Save sharing</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDelete ? (
        <div className="cb-modal-back">
          <div className="cb-modal" role="dialog" aria-modal="true">
            <h2>Delete this case?</h2>
            <p>{payer || 'This payer'} · claim {claim || record?.case_key} will be removed.</p>
            <div className="cb-modal__actions">
              <button type="button" className="dm-btn dm-btn--secondary" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button type="button" className="dm-btn dm-btn--primary" onClick={remove} disabled={busy}>Delete</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
